import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, Link, useLocation } from "react-router-dom";
import "./App.css";
import { LandingPage, CoursePage, CaseStudiesPage, CoursePlayerPage, ComingSoonPage, CcLicense } from "./pages/DpiPages";
import TeamPage from "./pages/TeamPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import UserDashboard from "./pages/UserDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import { BlogListPage, BlogPostPage } from "./pages/BlogPages";
import AuthCallback from "./auth/AuthCallback";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ProtectedRoute, AdminRoute } from "./auth/Guards";
import { Button } from "./components/ui/button";

function HeaderUserMenu() {
  const { user, logout, loading } = useAuth();
  if (loading) return null;
  if (!user) {
    return (
      <div className="flex items-center gap-1.5">
        <Button asChild size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" data-testid="header-login-btn">
          <Link to="/login">Sign in</Link>
        </Button>
        <Button asChild size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" data-testid="header-register-btn">
          <Link to="/register">Register</Link>
        </Button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5" data-testid="header-user-menu">
      {user.role === "admin" && (
        <Button asChild size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" data-testid="header-admin-link">
          <Link to="/admin">Admin</Link>
        </Button>
      )}
      <Button asChild size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" data-testid="header-me-link">
        <Link to="/me">My learning</Link>
      </Button>
      <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={logout} data-testid="header-logout-btn">
        Sign out
      </Button>
    </div>
  );
}

function AppLayout() {
  const [logoSrc, setLogoSrc] = useState("/dpial-logo.png");

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) return;
    fetch(`${backendUrl}/api/settings`)
      .then((r) => r.json())
      .then((d) => { if (d.site_logo) setLogoSrc(d.site_logo); })
      .catch(() => {});
  }, []);

  return (
    <div className="app-shell min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))]">
      <header className="app-header sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 md:px-6">
          <NavLink to="/" className="flex items-center gap-2">
            <img
              src={logoSrc}
              alt="DPI Applied Learning logo"
              className="h-12 w-auto rounded-sm object-contain md:h-14"
              data-testid="header-dpial-logo"
            />
            <img
              src="/iipp-logo.png"
              alt="UCL Institute for Innovation and Public Purpose logo"
              className="h-12 w-auto object-contain md:h-14"
              data-testid="header-iipp-logo"
            />
          </NavLink>
          <nav className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <NavLink to="/learn" className={({ isActive }) => `nav-link rounded-full px-3 py-1.5 text-xs md:text-sm ${isActive ? "bg-[#361a54] text-white" : "hover:bg-[#f5f0fa]/70 hover:text-[#361a54]"}`}>
              Learn DPI
            </NavLink>
            <NavLink to="/case-studies" className={({ isActive }) => `nav-link rounded-full px-3 py-1.5 text-xs md:text-sm ${isActive ? "bg-[#361a54] text-white" : "hover:bg-[#f5f0fa]/70 hover:text-[#361a54]"}`}>
              Case studies
            </NavLink>
            <NavLink to="/team" className={({ isActive }) => `nav-link rounded-full px-3 py-1.5 text-xs md:text-sm ${isActive ? "bg-[#361a54] text-white" : "hover:bg-[#f5f0fa]/70 hover:text-[#361a54]"}`}>
              About
            </NavLink>
            <NavLink to="/coming-soon" className={({ isActive }) => `nav-link rounded-full px-3 py-1.5 text-xs md:text-sm ${isActive ? "bg-[#361a54] text-white" : "hover:bg-[#f5f0fa]/70 hover:text-[#361a54]"}`}>
              Coming soon
            </NavLink>
          </nav>
          <HeaderUserMenu />
        </div>
      </header>

      <main className="mx-auto flex max-w-6xl flex-1 flex-col px-4 pb-10 pt-6 md:px-6 md:pb-16 md:pt-10">
        <AppRoutes />
      </main>

      <footer className="border-t border-border/60 bg-background/90">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-4 text-xs text-muted-foreground md:px-6">
          <CcLicense variant="footer" />
          <div className="flex flex-col gap-2 border-t border-border/40 pt-3 md:flex-row md:items-center md:justify-between">
            <p>
              DPI Governance Applied Learning · A collaboration between UCL Institute for Innovation and Public Purpose and Co-Develop.
            </p>
            <p className="flex gap-3">
              <a href="https://www.ucl.ac.uk/bartlett/public-purpose" target="_blank" rel="noreferrer" className="hover:text-[#361a54] hover:underline">UCL IIPP</a>
              <a href="https://codevelop.fund" target="_blank" rel="noreferrer" className="hover:text-[#361a54] hover:underline">Co-Develop</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Detect Google OAuth callback at the top of the route tree (synchronous)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/learn" element={<CoursePage />} />
      <Route path="/learn/:courseId" element={<CoursePage />} />
      <Route path="/learn/:courseId/play" element={<ProtectedRoute><CoursePlayerPage /></ProtectedRoute>} />
      <Route path="/case-studies" element={<CaseStudiesPage />} />
      <Route path="/coming-soon" element={<ComingSoonPage />} />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfilePage /></ProtectedRoute>} />
      <Route path="/me" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
